import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { heroActions } from '../Store/Actions/heroActions';
import { getHeroInventory, getHero } from '../Store/Selectors/selectors';
import { ITEM_TYPE_TO_SLOT, EQUIPMENT_SLOT } from '../Helpers/constants';
import { getEquipmentBonus, ALL_SLOTS } from '../Helpers/equipment';
import { Btn, Label, Rule } from './Common/OkoylUI';
import { log } from '../Helpers/logger';

const TAB = { INVENTORY: 'inventory', EQUIPMENT: 'equipment' };

const SLOT_ICON = {
    [EQUIPMENT_SLOT.WEAPON]:    '⚔',
    [EQUIPMENT_SLOT.ARMOR]:     '◈',
    [EQUIPMENT_SLOT.HELM]:      '◊',
    [EQUIPMENT_SLOT.ACCESSORY]: '○',
};

const InventoryBox = ({ onClose }) => {
    const [tab, setTab] = useState(TAB.INVENTORY);
    const [hovered, setHovered] = useState(null);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const hero = useSelector(getHero);
    const inventory = useSelector(getHeroInventory) || {};
    const equipment = hero?.equipment || {};
    const SLOT_LABEL = {
        [EQUIPMENT_SLOT.WEAPON]:    t('inventory.slots.weapon'),
        [EQUIPMENT_SLOT.ARMOR]:     t('inventory.slots.armor'),
        [EQUIPMENT_SLOT.HELM]:      t('inventory.slots.helm'),
        [EQUIPMENT_SLOT.ACCESSORY]: t('inventory.slots.accessory'),
    };

    const renderBonuses = (item) => {
        if (!item?.bonuses) return null;
        const lines = Object.entries(item.bonuses).map(([stat, val]) => `+${val} ${stat}`);
        return <div style={{ marginTop: 6, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--okoyl-300)' }}>{lines.join(' · ')}</div>;
    };

    const renderInventory = () => {
        const slots = Object.entries(inventory);
        if (!slots.length) {
            return <p className="oklore oklore-sm">{t('inventory.empty')}</p>;
        }
        return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(64px, 1fr))', gap: 10 }}>
                {slots.map(([slotKey, slot]) => {
                    if (!slot || !slot.item) {
                        return (
                            <div
                                key={slotKey}
                                style={{
                                    aspectRatio: '1',
                                    background: 'var(--ash-900)',
                                    border: '1px dashed rgba(200,144,42,0.18)',
                                    borderRadius: 'var(--r-md)',
                                }}
                            />
                        );
                    }
                    const item = slot.item;
                    const isEquipable = !!ITEM_TYPE_TO_SLOT[item.type];
                    const accent = item.consumable
                        ? 'var(--okoyl-200)'
                        : isEquipable
                        ? 'var(--neant-200)'
                        : 'var(--ash-300)';
                    return (
                        <button
                            key={slotKey}
                            type="button"
                            onClick={() => {
                                if (item.consumable) dispatch(heroActions.useItem(slotKey));
                                else if (isEquipable) dispatch(heroActions.equipItem(slotKey));
                                else log.ui.log('Item non actionnable', item);
                            }}
                            onMouseEnter={() => setHovered(item)}
                            onMouseLeave={() => setHovered(null)}
                            title={isEquipable ? t('inventory.tooltipEquip') : item.consumable ? t('inventory.tooltipUse') : item.name}
                            style={{
                                position: 'relative',
                                aspectRatio: '1',
                                background: 'var(--ash-900)',
                                border: 'none',
                                borderRadius: 'var(--r-md)',
                                boxShadow: 'inset 0 0 0 1px rgba(200,144,42,0.22), 0 1px 3px rgba(0,0,0,0.5)',
                                cursor: 'pointer',
                                color: accent,
                                fontSize: 28,
                                transition: 'box-shadow 200ms var(--ease-out), transform 200ms var(--ease-out)',
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 0 0 1px var(--okoyl-300), 0 2px 12px rgba(200,144,42,0.3)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(200,144,42,0.22), 0 1px 3px rgba(0,0,0,0.5)'; e.currentTarget.style.transform = 'none'; }}
                        >
                            <span>{item.icon}</span>
                            {item.stackable && (
                                <span style={{
                                    position: 'absolute',
                                    bottom: 2,
                                    right: 4,
                                    fontFamily: 'var(--font-mono)',
                                    fontSize: 10,
                                    color: 'var(--ash-200)',
                                    background: 'rgba(7,6,10,0.85)',
                                    padding: '0 4px',
                                    borderRadius: 'var(--r-sm)',
                                }}>
                                    {slot.quantity}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        );
    };

    const renderEquipment = () => {
        const bonus = getEquipmentBonus(hero);
        const bonusLines = Object.entries(bonus).filter(([, v]) => v !== 0).map(([k, v]) => `+${v} ${k}`);

        return (
            <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {ALL_SLOTS.map((slotType) => {
                        const item = equipment[slotType];
                        return (
                            <div
                                key={slotType}
                                onMouseEnter={() => item && setHovered(item)}
                                onMouseLeave={() => setHovered(null)}
                                style={{
                                    background: 'var(--ash-900)',
                                    border: '1px solid rgba(200,144,42,0.18)',
                                    borderRadius: 'var(--r-md)',
                                    padding: 12,
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span className="oklbl oklbl-muted">
                                        <span style={{ color: 'var(--okoyl-300)', marginRight: 4 }}>{SLOT_ICON[slotType]}</span>
                                        {SLOT_LABEL[slotType]}
                                    </span>
                                    {item && (
                                        <button
                                            type="button"
                                            onClick={() => dispatch(heroActions.unequipItem(slotType))}
                                            title={t('inventory.unequip')}
                                            style={{
                                                background: 'transparent',
                                                border: '1px solid rgba(192,57,43,0.5)',
                                                color: 'var(--crit)',
                                                fontFamily: 'var(--font-mono)',
                                                fontSize: 11,
                                                padding: '2px 8px',
                                                borderRadius: 'var(--r-sm)',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {t('inventory.unequip')}
                                        </button>
                                    )}
                                </div>
                                <div style={{ marginTop: 10, minHeight: 40 }}>
                                    {item ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <span style={{ fontSize: 28, color: 'var(--okoyl-200)' }}>{item.icon}</span>
                                            <span style={{ fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--okoyl-200)' }}>
                                                {item.name}
                                            </span>
                                        </div>
                                    ) : (
                                        <span style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 13, color: 'var(--fg-muted)' }}>
                                            {t('inventory.slotEmpty')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {bonusLines.length > 0 && (
                    <div style={{ marginTop: 14, padding: 12, background: 'rgba(200,144,42,0.08)', border: '1px solid rgba(200,144,42,0.32)', borderRadius: 'var(--r-md)' }}>
                        <Label color="gold">{t('inventory.blessings')}</Label>
                        <div style={{ marginTop: 6, fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--okoyl-200)' }}>
                            {bonusLines.join(' · ')}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="okcard" style={{ width: '100%', maxWidth: 720 }}>
            <div className="okcard-title" style={{ paddingRight: 40 }}>{t('inventory.title')}</div>

            <Rule />

            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <Btn
                    variant={tab === TAB.INVENTORY ? 'primary' : 'secondary'}
                    small
                    onClick={() => setTab(TAB.INVENTORY)}
                >
                    {t('inventory.tabs.inventory')}
                </Btn>
                <Btn
                    variant={tab === TAB.EQUIPMENT ? 'primary' : 'secondary'}
                    small
                    onClick={() => setTab(TAB.EQUIPMENT)}
                >
                    {t('inventory.tabs.equipment')}
                </Btn>
            </div>

            {tab === TAB.INVENTORY ? renderInventory() : renderEquipment()}

            {/* Always rendered (with a placeholder when nothing is hovered) so
                the modal height stays stable as the cursor moves over items. */}
            <div
                style={{
                    marginTop: 16,
                    padding: 14,
                    background: 'var(--ash-900)',
                    border: '1px solid rgba(200,144,42,0.18)',
                    borderRadius: 'var(--r-md)',
                    minHeight: 96,
                }}
            >
                {hovered ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 36 }}>{hovered.icon}</span>
                        <div>
                            <div style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--okoyl-200)' }}>
                                {hovered.name}
                            </div>
                            <div className="oklore oklore-sm" style={{ marginTop: 2 }}>
                                {hovered.description || t('inventory.noDescription')}
                            </div>
                            {renderBonuses(hovered)}
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 64, textAlign: 'center' }}>
                        <span className="oklore oklore-sm" style={{ color: 'var(--fg-muted)' }}>
                            {t('inventory.hover')}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InventoryBox;
